import { Prec, StateField } from '@codemirror/state'
import { Decoration, type DecorationSet, EditorView, keymap, WidgetType } from '@codemirror/view'
import React from 'react'
import { createPortal } from 'react-dom'

import type { PzExpression } from '@/app/component/atom/pz-expression'
import { type PzMetadata } from '@/app/component/atom/pz-metadata'
import PzPill from '@/app/component/atom/pz-pill'
import { parsePersonalization } from '@/app/module/utils/pz-parser'

type MountedWidget = {
  element: HTMLElement
  id: number
  expression: PzExpression
  details: PzMetadata
}

type RegisterWidget = (element: HTMLElement, details: PzMetadata, expression: PzExpression) => () => void
const widgetCleanups = new WeakMap<HTMLElement, () => void>()

class PersonalizationWidget extends WidgetType {
  constructor(
    readonly details: PzMetadata,
    readonly expression: PzExpression,
    readonly register: RegisterWidget,
  ) {
    super()
  }

  eq(other: PersonalizationWidget) {
    return (
      this.details === other.details &&
      this.expression.text === other.expression.text &&
      this.register === other.register
    )
  }

  toDOM() {
    const element = document.createElement('span')
    element.className = 'cm-pz-label'
    element.setAttribute('contenteditable', 'false')
    widgetCleanups.set(element, this.register(element, this.details, this.expression))
    return element
  }

  destroy(element: HTMLElement) {
    widgetCleanups.get(element)?.()
    widgetCleanups.delete(element)
  }
}

export const useCodePersonalization = (
  catalog: PzMetadata[],
  enabled: boolean,
  onInspect: (expression: PzExpression, trigger: HTMLElement) => void,
) => {
  const [widgets, setWidgets] = React.useState<MountedWidget[]>([])
  const nextId = React.useRef(0)
  const register = React.useCallback<RegisterWidget>((element, details, expression) => {
    const widget = { element, details, expression, id: nextId.current++ }
    setWidgets((current) => [...current, widget])
    return () => setWidgets((current) => current.filter((item) => item !== widget))
  }, [])

  const extension = React.useMemo(() => {
    if (!enabled) return []
    const byReference = new Map(catalog.map((item) => [item.value, item]))
    const references = new Set(byReference.keys())
    const decorate = (source: string) =>
      Decoration.set(
        parsePersonalization(source, references).map(({ from, to, reference, pipelineStages }) =>
          Decoration.replace({
            widget: new PersonalizationWidget(
              byReference.get(reference)!,
              { reference, pipelineStages, text: source.slice(from, to) },
              register,
            ),
          }).range(from, to),
        ),
      )
    // A state field supplies replacements before layout, including expressions spanning line breaks.
    const field = StateField.define<DecorationSet>({
      create: (state) => decorate(state.doc.toString()),
      update: (value, transaction) => (transaction.docChanged ? decorate(transaction.newDoc.toString()) : value),
      provide: (stateField) => [
        EditorView.decorations.from(stateField),
        EditorView.atomicRanges.of((view) => view.state.field(stateField)),
      ],
    })
    // Switching out of Advanced mode can leave a selection inside a now-hidden expression.
    const remove = (view: EditorView, forward: boolean) => {
      if (view.state.readOnly) return false
      if (view.state.selection.ranges.length !== 1) return false
      const ranges = view.state.field(field)
      const range = view.state.selection.main
      let { from, to } = range
      let touchesLabel = false
      ranges.between(from, to, (start, end) => {
        const intersects = range.empty
          ? (start < from && end > from) || (forward ? start === from : end === from)
          : start < to && end > from
        if (!intersects) return
        touchesLabel = true
        from = Math.min(from, start)
        to = Math.max(to, end)
      })
      if (!touchesLabel) return false
      view.dispatch({ changes: { from, to }, selection: { anchor: from }, userEvent: 'delete', scrollIntoView: true })
      return true
    }
    return [
      field,
      Prec.highest(
        keymap.of([
          { key: 'Backspace', run: (view) => remove(view, false) },
          { key: 'Delete', run: (view) => remove(view, true) },
        ]),
      ),
    ]
  }, [catalog, enabled, register])

  const portals = widgets.map(({ details, element, expression, id }) =>
    createPortal(
      <PzPill
        hasModifiers={expression.pipelineStages.length > 0}
        label={details.label}
        onInspect={(trigger) => onInspect(expression, trigger)}
      />,
      element,
      String(id),
    ),
  )

  return { extension, portals }
}
